import type {
  OpsCreateTicketReplyResponse,
  OpsListTicketsResponse,
  OpsTicketAttachmentDownloadResponse,
  OpsTicketDetailResponse,
  OpsUpdateTicketStatusResponse,
  TicketStatus,
} from '@nadoutong/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  createVendorReply,
  downloadTicketAttachment,
  getTicket,
  listTickets,
  updateTicketStatus,
} from './supportTickets.ts';
import type { OpsTicketsQuery } from './supportTickets.ts';

/**
 * React Query hooks for `/v1/ops/tickets*` (#1394).
 *
 * List keying includes the full query tuple so distinct filter
 * combinations don't alias in the cache; the detail key hangs off the
 * same root so a single root invalidation refreshes both after a
 * mutation (a status change moves the queue row AND the detail header;
 * a vendor reply bumps `updated_at` on the row AND appends to the
 * conversation).
 */

export const SUPPORT_TICKETS_ROOT_KEY = ['support', 'tickets'] as const;

export function supportTicketsQueryKey(
  query: OpsTicketsQuery,
): readonly ['support', 'tickets', 'list', OpsTicketsQuery] {
  return [...SUPPORT_TICKETS_ROOT_KEY, 'list', query] as const;
}

export function supportTicketQueryKey(
  ticketId: string,
): readonly ['support', 'tickets', 'detail', string] {
  return [...SUPPORT_TICKETS_ROOT_KEY, 'detail', ticketId] as const;
}

export function useSupportTickets(
  query: OpsTicketsQuery,
): UseQueryResult<OpsListTicketsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: supportTicketsQueryKey(query),
    queryFn: ({ signal }) => listTickets(query, fetchImpl, signal),
    // The sort key is part of the query key, so every header click is a cache
    // miss. Without this the table unmounts to a skeleton and remounts —
    // which drops keyboard focus off the header that was just activated, and
    // makes the `disabled={query.isFetching}` guard on those headers
    // unreachable because there is no header to disable.
    placeholderData: keepPreviousData,
  });
}

export function useSupportTicket(
  ticketId: string,
): UseQueryResult<OpsTicketDetailResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: supportTicketQueryKey(ticketId),
    queryFn: ({ signal }) => getTicket(ticketId, fetchImpl, signal),
    enabled: ticketId.length > 0,
  });
}

/**
 * The bytes of one attachment, base64, from
 * `/v1/ops/tickets/attachments/:id`.
 *
 * Deliberately keyed OUTSIDE `SUPPORT_TICKETS_ROOT_KEY`: an attachment's
 * object is immutable once the create transaction commits, so a status
 * change or a vendor reply — both of which invalidate that root — must
 * not force the images to be downloaded again. `staleTime: Infinity`
 * for the same reason.
 */
export const SUPPORT_TICKET_ATTACHMENT_ROOT_KEY = ['support', 'ticket-attachment'] as const;

export function supportTicketAttachmentQueryKey(
  attachmentId: string,
): readonly ['support', 'ticket-attachment', string] {
  return [...SUPPORT_TICKET_ATTACHMENT_ROOT_KEY, attachmentId] as const;
}

export function useTicketAttachment(
  attachmentId: string,
  enabled: boolean,
): UseQueryResult<OpsTicketAttachmentDownloadResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: supportTicketAttachmentQueryKey(attachmentId),
    queryFn: () => downloadTicketAttachment(attachmentId, fetchImpl),
    enabled: enabled && attachmentId.length > 0,
    staleTime: Infinity,
  });
}

export function useUpdateTicketStatus(): UseMutationResult<
  OpsUpdateTicketStatusResponse,
  Error,
  { ticketId: string; status: TicketStatus }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, status }) => updateTicketStatus(ticketId, { status }, fetchImpl),
    onSuccess: () => {
      // Root invalidation covers the list AND every detail entry.
      void qc.invalidateQueries({ queryKey: SUPPORT_TICKETS_ROOT_KEY });
    },
  });
}

export function useCreateVendorReply(): UseMutationResult<
  OpsCreateTicketReplyResponse,
  Error,
  { ticketId: string; content: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, content }) => createVendorReply(ticketId, { content }, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPORT_TICKETS_ROOT_KEY });
    },
  });
}
